import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import useAuth from '../hooks/useAuth';
import { PATH_DASHBOARD } from '../routes/paths';
import { getEinProblemCopy, shouldRemindAboutEin } from '../utils/einStatus';

// ----------------------------------------------------------------------

const SNOOZE_KEY_PREFIX = 'ein_reminder_snoozed_';
const SNOOZE_DURATION = 3 * 24 * 60 * 60 * 1000; // 3 days

const getSnoozeKey = (teamId) =>
  teamId ? `${SNOOZE_KEY_PREFIX}${teamId}` : null;

const isSnoozed = (teamId) => {
  try {
    const key = getSnoozeKey(teamId);
    if (!key) return false;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const { snoozedAt } = JSON.parse(raw);
    return Date.now() - snoozedAt < SNOOZE_DURATION;
  } catch {
    return false;
  }
};

const snooze = (teamId) => {
  try {
    const key = getSnoozeKey(teamId);
    if (!key) return;
    localStorage.setItem(key, JSON.stringify({ snoozedAt: Date.now() }));
  } catch {
    // Storage unavailable (private mode, quota) - the dialog simply reappears.
  }
};

export default function EinReminderDialog() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const history = useHistory();
  const [open, setOpen] = useState(false);

  const shouldRemind = shouldRemindAboutEin(user);

  useEffect(() => {
    if (!shouldRemind) {
      setOpen(false);
      return;
    }
    setOpen(!isSnoozed(user?.team_id));
  }, [shouldRemind, user?.team_id, user?.ein_status]);

  if (!shouldRemind) return null;

  const copy = getEinProblemCopy(user.ein_status);

  const handleSnooze = () => {
    snooze(user.team_id);
    setOpen(false);
  };

  const handleFix = () => {
    snooze(user.team_id);
    setOpen(false);
    history.push(PATH_DASHBOARD.settings.business('settings', 'tax-settings'));
  };

  return (
    <Dialog open={open} onClose={handleSnooze} fullWidth maxWidth="sm">
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{copy.body}</DialogContentText>
        {copy.selfServiceable ? null : (
          <Alert severity="info">
            <Trans
              i18nKey="ein-reminder.registered-to"
              values={{
                name:
                  user.ein_registered_name ||
                  t('ein-reminder.different-organization'),
              }}
              components={{ bold: <strong /> }}
            />
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSnooze}>
          {t('ein-reminder.remind-me-later')}
        </Button>
        {copy.selfServiceable && user.is_admin ? (
          <Button variant="contained" onClick={handleFix}>
            {t('ein-reminder.update-ein')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
